import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  AlertTriangle, UserX, BookX, Calendar, Plus, 
  CheckCircle, Clock, RefreshCw, FileText, GraduationCap,
  ClipboardList, Search
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/api/api';
const FailedStudentManagement = () => {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({});
  const [reexams, setReexams] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Filters
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialogs
  const [addRecordDialog, setAddRecordDialog] = useState(false);
  const [addReexamDialog, setAddReexamDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  
  // Form data
  const [students, setStudents] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [subjects, setSubjects] = useState([]);
  
  const [newRecord, setNewRecord] = useState({
    student_id: '',
    semester_id: '',
    academic_year: new Date().getFullYear().toString(),
    failure_type: 'atkt',
    failed_subjects: [],
    remarks: ''
  });
  
  const [newReexam, setNewReexam] = useState({
    student_id: '',
    subject_id: '',
    exam_date: '',
    exam_type: 'supplementary',
    max_marks: 100,
    passing_marks: 40
  });

  useEffect(() => {
    fetchData();
    fetchDropdowns();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [recordsRes, summaryRes, reexamsRes] = await Promise.all([
        api.get('/failed-students/records'),
        api.get('/failed-students/summary'),
        api.get('/failed-students/reexams')
      ]);
      
      setRecords(recordsRes.data.records || []);
      setSummary(summaryRes.data || {});
      setReexams(reexamsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const [studentsRes, semestersRes, subjectsRes] = await Promise.all([
        api.get('/students/dropdown'),
        api.get('/master-settings/semesters'),
        api.get('/master-settings/subjects')
      ]);
      
      const studentsData = Array.isArray(studentsRes.data) ? studentsRes.data : (studentsRes.data?.data || []);
      const semestersData = Array.isArray(semestersRes.data) ? semestersRes.data : (semestersRes.data?.data || []);
      const subjectsData = Array.isArray(subjectsRes.data) ? subjectsRes.data : (subjectsRes.data?.data || []);
      
      setStudents(studentsData);
      setSemesters(semestersData);
      setSubjects(subjectsData);
    } catch (error) {
      console.error('Error fetching dropdowns:', error);
    }
  };

  const handleCreateRecord = async () => {
    if (!newRecord.student_id || !newRecord.semester_id || newRecord.failed_subjects.length === 0) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      await api.post('/failed-students/record', newRecord);
      toast.success('Failed student record created');
      setAddRecordDialog(false);
      setNewRecord({
        student_id: '',
        semester_id: '',
        academic_year: new Date().getFullYear().toString(),
        failure_type: 'atkt',
        failed_subjects: [],
        remarks: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error creating record:', error);
      toast.error(error.response?.data?.detail || 'Failed to create record');
    }
  };

  const handleCreateReexam = async () => {
    if (!newReexam.student_id || !newReexam.subject_id || !newReexam.exam_date) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      await api.post('/failed-students/reexam', newReexam);
      toast.success('Re-examination scheduled');
      setAddReexamDialog(false);
      setNewReexam({
        student_id: '',
        subject_id: '',
        exam_date: '',
        exam_type: 'supplementary',
        max_marks: 100,
        passing_marks: 40
      });
      fetchData();
    } catch (error) {
      console.error('Error scheduling reexam:', error);
      toast.error(error.response?.data?.detail || 'Failed to schedule re-exam');
    }
  };

  const handleUpdateStatus = async (recordId, newStatus) => {
    try {
      await api.put(`/failed-students/record/${recordId}/status`, { status: newStatus });
      toast.success(`Record marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateReexamResult = async (reexamId, result) => {
    try {
      await api.put(`/failed-students/reexam/${reexamId}/result`, result);
      toast.success('Result updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update result');
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'year_back': return 'bg-red-100 text-red-700';
      case 'atkt': return 'bg-amber-100 text-amber-700';
      case 'detention': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'cleared': return 'bg-green-100 text-green-700';
      case 'promoted': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredRecords = records.filter(r => {
    if (filterType && filterType !== 'all' && r.failure_type !== filterType) return false;
    if (filterStatus && filterStatus !== 'all' && r.status !== filterStatus) return false;
    if (searchTerm && !r.student_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="failed-student-management">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <UserX className="text-red-500" size={28} />
            Failed Student Management
          </h1>
          <p className="text-gray-600 mt-1">Manage Year Back, ATKT, Detention & Re-examinations</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Dialog open={addRecordDialog} onOpenChange={setAddRecordDialog}>
            <DialogTrigger asChild>
              <Button className="bg-red-600 hover:bg-red-700">
                <Plus className="h-4 w-4 mr-2" /> Add Failed Record
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Add Failed Student Record</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Student *</Label>
                  <Select value={newRecord.student_id} onValueChange={(v) => setNewRecord({...newRecord, student_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
                    <SelectContent>
                      {students.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.full_name} ({s.student_id})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Semester *</Label>
                  <Select value={newRecord.semester_id} onValueChange={(v) => setNewRecord({...newRecord, semester_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select semester" /></SelectTrigger>
                    <SelectContent>
                      {semesters.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Failure Type *</Label>
                  <Select value={newRecord.failure_type} onValueChange={(v) => setNewRecord({...newRecord, failure_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="atkt">ATKT (Allowed To Keep Terms)</SelectItem>
                      <SelectItem value="year_back">Year Back / Detention</SelectItem>
                      <SelectItem value="detention">Detention</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Failed Subjects *</Label>
                  <Select 
                    value={newRecord.failed_subjects[0] || ''} 
                    onValueChange={(v) => {
                      if (!newRecord.failed_subjects.includes(v)) {
                        setNewRecord({...newRecord, failed_subjects: [...newRecord.failed_subjects, v]});
                      }
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder="Add subjects" /></SelectTrigger>
                    <SelectContent>
                      {subjects.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name} ({s.code})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {newRecord.failed_subjects.map(subId => {
                      const sub = subjects.find(s => s.id === subId);
                      return sub ? (
                        <Badge key={subId} variant="secondary" className="cursor-pointer" onClick={() => {
                          setNewRecord({...newRecord, failed_subjects: newRecord.failed_subjects.filter(id => id !== subId)});
                        }}>
                          {sub.code} ✕
                        </Badge>
                      ) : null;
                    })}
                  </div>
                </div>
                <div>
                  <Label>Academic Year</Label>
                  <Input 
                    value={newRecord.academic_year} 
                    onChange={(e) => setNewRecord({...newRecord, academic_year: e.target.value})}
                    placeholder="2024-25"
                  />
                </div>
                <div>
                  <Label>Remarks</Label>
                  <Input 
                    value={newRecord.remarks} 
                    onChange={(e) => setNewRecord({...newRecord, remarks: e.target.value})}
                    placeholder="Optional remarks"
                  />
                </div>
                <Button onClick={handleCreateRecord} className="w-full bg-red-600 hover:bg-red-700">
                  Create Record
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 text-center">
            <UserX className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-red-600">{summary.summary?.year_back?.total || 0}</p>
            <p className="text-sm text-gray-600">Year Back</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-center">
            <BookX className="h-8 w-8 text-amber-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-amber-600">{summary.summary?.atkt?.total || 0}</p>
            <p className="text-sm text-gray-600">ATKT</p>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="h-8 w-8 text-orange-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-orange-600">{summary.summary?.detention?.total || 0}</p>
            <p className="text-sm text-gray-600">Detention</p>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4 text-center">
            <Clock className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-yellow-600">{summary.total_pending || 0}</p>
            <p className="text-sm text-gray-600">Pending</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4 text-center">
            <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-600">{summary.total_cleared || 0}</p>
            <p className="text-sm text-gray-600">Cleared</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4 text-center">
            <Calendar className="h-8 w-8 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-600">{summary.upcoming_reexams || 0}</p>
            <p className="text-sm text-gray-600">Upcoming Exams</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">
            <ClipboardList className="h-4 w-4 mr-2" /> Failed Records
          </TabsTrigger>
          <TabsTrigger value="reexams">
            <FileText className="h-4 w-4 mr-2" /> Re-examinations
          </TabsTrigger>
        </TabsList>

        {/* Failed Records Tab */}
        <TabsContent value="overview" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <CardTitle>Failed Student Records</CardTitle>
                <div className="flex gap-2 flex-wrap">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input 
                      placeholder="Search student..." 
                      className="pl-9 w-48"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-36"><SelectValue placeholder="All Types" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="year_back">Year Back</SelectItem>
                      <SelectItem value="atkt">ATKT</SelectItem>
                      <SelectItem value="detention">Detention</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-32"><SelectValue placeholder="All Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="cleared">Cleared</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredRecords.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <GraduationCap className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No failed student records found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 text-sm font-medium">Student</th>
                        <th className="text-left p-3 text-sm font-medium">Type</th>
                        <th className="text-left p-3 text-sm font-medium">Failed Subjects</th>
                        <th className="text-left p-3 text-sm font-medium">Year</th>
                        <th className="text-left p-3 text-sm font-medium">Status</th>
                        <th className="text-center p-3 text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRecords.map(record => (
                        <tr key={record.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <p className="font-medium">{record.student_name}</p>
                            <p className="text-sm text-gray-500">{record.student_roll}</p>
                          </td>
                          <td className="p-3">
                            <Badge className={getTypeColor(record.failure_type)}>
                              {record.failure_type === 'year_back' ? 'Year Back' : 
                               record.failure_type === 'atkt' ? 'ATKT' : 'Detention'}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <div className="flex flex-wrap gap-1">
                              {record.failed_subjects?.slice(0, 3).map((sub, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {sub.code || sub.name}
                                </Badge>
                              ))}
                              {record.failed_subjects?.length > 3 && (
                                <Badge variant="outline" className="text-xs">
                                  +{record.failed_subjects.length - 3}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-sm">{record.academic_year}</td>
                          <td className="p-3">
                            <Badge className={getStatusColor(record.status)}>
                              {record.status}
                            </Badge>
                          </td>
                          <td className="p-3 text-center">
                            {record.status === 'pending' && (
                              <div className="flex gap-1 justify-center">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-green-600"
                                  onClick={() => handleUpdateStatus(record.id, 'cleared')}
                                >
                                  Clear
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    setNewReexam({...newReexam, student_id: record.student_id});
                                    setAddReexamDialog(true);
                                  }}
                                >
                                  Schedule Exam
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Re-examinations Tab */}
        <TabsContent value="reexams" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Re-examinations</CardTitle>
                <Dialog open={addReexamDialog} onOpenChange={setAddReexamDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="h-4 w-4 mr-2" /> Schedule Re-exam
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Schedule Re-examination</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Student *</Label>
                        <Select value={newReexam.student_id} onValueChange={(v) => setNewReexam({...newReexam, student_id: v})}>
                          <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
                          <SelectContent>
                            {students.map(s => (
                              <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Subject *</Label>
                        <Select value={newReexam.subject_id} onValueChange={(v) => setNewReexam({...newReexam, subject_id: v})}>
                          <SelectTrigger><SelectValue placeholder="Select subject" /></SelectTrigger>
                          <SelectContent>
                            {subjects.map(s => (
                              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Exam Date *</Label>
                        <Input 
                          type="date" 
                          value={newReexam.exam_date}
                          onChange={(e) => setNewReexam({...newReexam, exam_date: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Exam Type</Label>
                        <Select value={newReexam.exam_type} onValueChange={(v) => setNewReexam({...newReexam, exam_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="supplementary">Supplementary</SelectItem>
                            <SelectItem value="improvement">Improvement</SelectItem>
                            <SelectItem value="regular">Regular</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Max Marks</Label>
                          <Input 
                            type="number" 
                            value={newReexam.max_marks}
                            onChange={(e) => setNewReexam({...newReexam, max_marks: parseInt(e.target.value)})}
                          />
                        </div>
                        <div>
                          <Label>Passing Marks</Label>
                          <Input 
                            type="number" 
                            value={newReexam.passing_marks}
                            onChange={(e) => setNewReexam({...newReexam, passing_marks: parseInt(e.target.value)})}
                          />
                        </div>
                      </div>
                      <Button onClick={handleCreateReexam} className="w-full">
                        Schedule Exam
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {reexams.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No re-examinations scheduled</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 text-sm font-medium">Student</th>
                        <th className="text-left p-3 text-sm font-medium">Subject</th>
                        <th className="text-left p-3 text-sm font-medium">Date</th>
                        <th className="text-left p-3 text-sm font-medium">Type</th>
                        <th className="text-left p-3 text-sm font-medium">Marks</th>
                        <th className="text-left p-3 text-sm font-medium">Result</th>
                        <th className="text-center p-3 text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reexams.map(exam => (
                        <tr key={exam.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <p className="font-medium">{exam.student_name}</p>
                            <p className="text-sm text-gray-500">{exam.student_roll}</p>
                          </td>
                          <td className="p-3">
                            <p className="font-medium">{exam.subject_name}</p>
                            <p className="text-sm text-gray-500">{exam.subject_code}</p>
                          </td>
                          <td className="p-3">{exam.exam_date}</td>
                          <td className="p-3">
                            <Badge variant="outline" className="capitalize">{exam.exam_type}</Badge>
                          </td>
                          <td className="p-3">
                            {exam.marks_obtained !== null ? (
                              <span className={exam.marks_obtained >= exam.passing_marks ? 'text-green-600' : 'text-red-600'}>
                                {exam.marks_obtained}/{exam.max_marks}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="p-3">
                            {exam.result_status ? (
                              <Badge className={
                                exam.result_status === 'passed' ? 'bg-green-100 text-green-700' :
                                exam.result_status === 'failed' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }>
                                {exam.result_status}
                              </Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {exam.status === 'scheduled' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  const marks = prompt('Enter marks obtained:');
                                  if (marks !== null) {
                                    const marksNum = parseInt(marks);
                                    handleUpdateReexamResult(exam.id, {
                                      marks_obtained: marksNum,
                                      status: marksNum >= exam.passing_marks ? 'passed' : 'failed'
                                    });
                                  }
                                }}
                              >
                                Enter Result
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FailedStudentManagement;
